import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Pencil, Upload, Wine, Search, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const COLOURS = ["Red", "White", "Rosé", "Sparkling", "Dessert", "Fortified"];

const emptyForm = { name: "", producer: "", region: "", grape: "", year: "", colour: "", glass: "", tasting_notes: "", description: "", food_pairings: "" };

export default function WinePage() {
  const [wines, setWines] = useState([]);
  const [search, setSearch] = useState("");
  const [filterColour, setFilterColour] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await axios.get(`${API}/wines`);
    setWines(data);
  };
  useEffect(() => { load(); }, []);

  const filtered = wines.filter(w => {
    const q = search.toLowerCase();
    const matchSearch = !q || w.name.toLowerCase().includes(q) || (w.producer || "").toLowerCase().includes(q) || (w.region || "").toLowerCase().includes(q) || (w.grape || "").toLowerCase().includes(q);
    const matchColour = filterColour === "all" || w.colour === filterColour;
    return matchSearch && matchColour;
  });

  const regions = [...new Set(wines.map(w => w.region).filter(Boolean))].sort();

  const openCreate = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (w) => { setEditing(w); setForm({ name: w.name, producer: w.producer || "", region: w.region || "", grape: w.grape || "", year: w.year || "", colour: w.colour || "", glass: w.glass || "", tasting_notes: w.tasting_notes || "", description: w.description || "", food_pairings: w.food_pairings || "" }); setDialogOpen(true); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/wines/${editing.id}`, form);
        toast.success("Wine updated");
      } else {
        await axios.post(`${API}/wines`, form);
        toast.success("Wine added");
      }
      setDialogOpen(false);
      load();
    } catch { toast.error("Failed to save"); }
    setSaving(false);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this wine?")) return;
    await axios.delete(`${API}/wines/${id}`);
    toast.success("Wine deleted");
    load();
  };

  const uploadImage = async (wineId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    await axios.post(`${API}/wines/${wineId}/upload-image`, fd);
    toast.success("Image uploaded");
    load();
  };

  const uploadCSV = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await axios.post(`${API}/wines/upload-csv`, fd);
      toast.success(data.message);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "CSV upload failed"); }
    e.target.value = "";
  };

  const F = (key, val) => setForm(p => ({ ...p, [key]: val }));

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-[hsl(var(--foreground))]">Wine Library</h1>
        <div className="flex items-center gap-2">
          <label className="cursor-pointer">
            <input type="file" accept=".csv" onChange={uploadCSV} className="hidden" data-testid="wine-csv-upload" />
            <Button variant="outline" size="sm" asChild><span><Upload size={14} className="mr-1" /> CSV</span></Button>
          </label>
          <Button size="sm" onClick={openCreate} data-testid="add-wine-btn"><Plus size={14} className="mr-1" /> Add Wine</Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="flex-1 relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search wines..." className="pl-9 bg-[hsl(var(--secondary))]" data-testid="wine-search" />
        </div>
        <Select value={filterColour} onValueChange={setFilterColour}>
          <SelectTrigger className="w-[140px] bg-[hsl(var(--secondary))]" data-testid="wine-colour-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Colours</SelectItem>
            {COLOURS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Wine list */}
      <div className="grid gap-3">
        {filtered.length === 0 && <p className="text-center py-8 text-sm text-[hsl(var(--muted-foreground))]">{wines.length === 0 ? "No wines yet. Add one or upload a CSV." : "No wines match your search."}</p>}
        {filtered.map(w => (
          <div key={w.id} className="flex items-center gap-3 p-3 rounded-lg bg-[hsl(var(--secondary))] border border-[hsl(var(--border))]" data-testid={`wine-item-${w.id}`}>
            <div className="w-12 h-12 rounded-lg overflow-hidden shrink-0 bg-[hsl(var(--background))] flex items-center justify-center border border-[hsl(var(--border))]">
              {w.image_url ? <img src={`${BACKEND}${w.image_url}`} alt={w.name} className="w-full h-full object-cover" /> : <Wine size={20} className="text-[hsl(var(--muted-foreground))]" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm text-[hsl(var(--foreground))] truncate">{w.name}</p>
              <p className="text-xs text-[hsl(var(--muted-foreground))] truncate">{[w.producer, w.region, w.year, w.colour].filter(Boolean).join(" · ")}</p>
            </div>
            <label className="cursor-pointer shrink-0">
              <input type="file" accept="image/*" onChange={e => e.target.files[0] && uploadImage(w.id, e.target.files[0])} className="hidden" />
              <Button variant="ghost" size="icon" asChild><span><ImageIcon size={14} /></span></Button>
            </label>
            <Button variant="ghost" size="icon" onClick={() => openEdit(w)} data-testid={`edit-wine-${w.id}`}><Pencil size={14} /></Button>
            <Button variant="ghost" size="icon" onClick={() => remove(w.id)} data-testid={`delete-wine-${w.id}`}><Trash2 size={14} className="text-red-400" /></Button>
          </div>
        ))}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto bg-[hsl(var(--card))] border-[hsl(var(--border))]">
          <DialogHeader><DialogTitle>{editing ? "Edit Wine" : "Add Wine"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name *</Label><Input value={form.name} onChange={e => F("name", e.target.value)} className="bg-[hsl(var(--secondary))]" data-testid="wine-name-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Producer</Label><Input value={form.producer} onChange={e => F("producer", e.target.value)} className="bg-[hsl(var(--secondary))]" /></div>
              <div><Label>Region</Label><Input value={form.region} onChange={e => F("region", e.target.value)} className="bg-[hsl(var(--secondary))]" list="wine-regions" /><datalist id="wine-regions">{regions.map(r => <option key={r} value={r} />)}</datalist></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Grape</Label><Input value={form.grape} onChange={e => F("grape", e.target.value)} className="bg-[hsl(var(--secondary))]" /></div>
              <div><Label>Year</Label><Input value={form.year} onChange={e => F("year", e.target.value)} className="bg-[hsl(var(--secondary))]" /></div>
              <div>
                <Label>Colour</Label>
                <Select value={form.colour} onValueChange={v => F("colour", v)}>
                  <SelectTrigger className="bg-[hsl(var(--secondary))]"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{COLOURS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Glass</Label><Input value={form.glass} onChange={e => F("glass", e.target.value)} className="bg-[hsl(var(--secondary))]" /></div>
            <div><Label>Tasting Notes</Label><Textarea value={form.tasting_notes} onChange={e => F("tasting_notes", e.target.value)} className="bg-[hsl(var(--secondary))]" /></div>
            <div><Label>Guest Description</Label><Textarea value={form.description} onChange={e => F("description", e.target.value)} placeholder="How staff should describe this wine..." className="bg-[hsl(var(--secondary))]" /></div>
            <div><Label>Food Pairings</Label><Textarea value={form.food_pairings} onChange={e => F("food_pairings", e.target.value)} className="bg-[hsl(var(--secondary))]" /></div>
            <Button onClick={save} disabled={saving} className="w-full" data-testid="wine-save-btn">{saving ? "Saving..." : editing ? "Update Wine" : "Add Wine"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
